import React, { useEffect, useRef, useState } from "react";
import ProgressBar from "progressbar.js";
import DietPopup, { Diet } from "./common/DietPopup";

export interface UserProfileData {
  firstName: string;
  lastName: string;
  firstTimeDashboard: boolean;
  imc: number;
  recommendedCaloriesPerDay: number;
  recommendedDailyWater: number;
  recommendedDailyProteins: number;
  recommendedDailyCarbos: number;
  recommendedDailyFats: number;
  email: string;
  selectedDiet?: Diet;
}

interface UserProfileProps {
  user: UserProfileData;
}

const DEFAULT_DIET: Diet = {
  title: "Dieta Equilibrada 🌱🔄",
  carb: "45-55%",
  protein: "15-25%",
  fats: "20-35%",
  description:
    "La más recomendada para quienes buscan un enfoque saludable y sostenible.",
};

const UserProfile: React.FC<UserProfileProps> = ({ user }) => {
  // Si no hay dieta seleccionada, usamos la por defecto
  const initialDiet = user.selectedDiet ? user.selectedDiet : DEFAULT_DIET;
  const [profile, setProfile] = useState<UserProfileData>({
    ...user,
    selectedDiet: initialDiet,
  });
  const [selectedDiet, setSelectedDiet] = useState<Diet>(initialDiet);

  const container2Ref = useRef<HTMLDivElement>(null);
  // Instancias de gráficos lineales (ProgressBar.Line)
  const proteinBarInstance = useRef<ProgressBar.Line | null>(null);
  const carboBarInstance = useRef<ProgressBar.Line | null>(null);
  const fatsBarInstance = useRef<ProgressBar.Line | null>(null);
  // Instancia del gráfico circular
  const circleInstance = useRef<ProgressBar.Circle | null>(null);
  const [openDietPopup, setOpenDietPopup] = useState(false);

  // Actualiza el estado local cuando cambia el prop "user"
  useEffect(() => {
    const newDiet = user.selectedDiet ? user.selectedDiet : DEFAULT_DIET;
    setProfile({ ...user, selectedDiet: newDiet });
    setSelectedDiet(newDiet);
  }, [user]);

  // Cada vez que "profile" cambie se guarda en localStorage
  useEffect(() => {
    if (profile.email) {
      const storageKey = "userProfileData_" + profile.email;
      localStorage.setItem(storageKey, JSON.stringify(profile));
      console.log("Perfil guardado:", profile);
    }
  }, [profile]);

  // Parsea un rango porcentual (por ejemplo, "45-55%") y devuelve el promedio exacto
  const parsePercentageRange = (rangeStr: string): number => {
    const cleanStr = rangeStr.replace("%", "");
    const parts = cleanStr.split("-");
    if (parts.length === 2) {
      const low = parseFloat(parts[0]);
      const high = parseFloat(parts[1]);
      return (low + high) / 2;
    }
    return parseFloat(cleanStr);
  };

  const getIMCAnimation = (imc: number): [number, string, string] => {
    if (imc < 18.5) {
      return [0.9, "Delgadez", "#85E0FF"];
    } else if (imc >= 18.5 && imc <= 24.9) {
      return [0.8, "Peso Normal", "#6F975D"];
    } else if (imc >= 25 && imc <= 29.9) {
      return [0.65, "Sobrepeso", "#FFEA82"];
    } else if (imc >= 30 && imc <= 34.9) {
      return [0.43, "Obesidad grado 1", "#6F975D"];
    } else if (imc >= 35 && imc <= 39.9) {
      return [0.3, "Obesidad grado 2", "#6F975D"];
    } else {
      return [0.2, "Obesidad grado 3", "#6F975D"];
    }
  };

  // Inicializa el gráfico circular del IMC (una sola vez)
  const initIMCCircle = () => {
    if (container2Ref.current && !circleInstance.current) {
      const imcArray = getIMCAnimation(profile.imc);
      circleInstance.current = new ProgressBar.Circle(container2Ref.current, {
        color: "#aaa",
        strokeWidth: 9,
        trailWidth: 1,
        easing: "easeInOut",
        duration: 3400,
        text: { autoStyleContainer: false, value: "" },
        from: { color: "#ff1f1f", width: 2 },
        to: { color: imcArray[2], width: 9 },
        step: (state, inst) => {
          inst.path.setAttribute("stroke", state.color);
          inst.path.setAttribute("stroke-width", state.width.toString());
          inst.setText(
            profile.imc === 0
              ? ""
              : `<span style="font-weight:bold; display:block !important">${profile.imc}</span><span>IMC</span>`
          );
        },
      });
      circleInstance.current.text.style.position = "absolute";
      circleInstance.current.text.style.left = "50%";
      circleInstance.current.text.style.top = "50%";
      circleInstance.current.text.style.transform = "translate(-50%, -50%)";
      circleInstance.current.text.style.padding = "0px";
      circleInstance.current.text.style.margin = "0px";
      circleInstance.current.text.style.color = "rgb(20, 20, 20)";
      circleInstance.current.text.style.fontFamily = '"Readex Pro", sans-serif';
      circleInstance.current.text.style.fontSize = "1.2em";
      circleInstance.current.text.style.fontWeight = "lighter";
      circleInstance.current.text.style.zIndex = "1001";
      circleInstance.current.animate(imcArray[0]);
    }
  };

  const updateIMCCircle = () => {
    if (circleInstance.current) {
      const imcArray = getIMCAnimation(profile.imc);
      circleInstance.current.animate(imcArray[0]);
      circleInstance.current.text.innerHTML =
        profile.imc === 0
          ? ""
          : `<span style="font-weight:bold">${profile.imc}</span><span>IMC</span>`;
    }
  };

  // Inicializamos las instancias de los gráficos lineales, configurándolos para mostrar texto
  useEffect(() => {
    if (!proteinBarInstance.current && document.getElementById("proteinBar")) {
      proteinBarInstance.current = new ProgressBar.Line(
        document.getElementById("proteinBar") as HTMLElement,
        {
          strokeWidth: 3,
          easing: "easeInOut",
          duration: 2100,
          color: "#5473FF",
          trailColor: "#eee",
          trailWidth: 1,
          svgStyle: { width: "100%", height: "100%" },
          text: { autoStyleContainer: false, value: "" },
        }
      );
    }
    if (!carboBarInstance.current && document.getElementById("carboBar")) {
      carboBarInstance.current = new ProgressBar.Line(
        document.getElementById("carboBar") as HTMLElement,
        {
          strokeWidth: 3,
          easing: "easeInOut",
          duration: 2100,
          color: "#FD9009",
          trailColor: "#eee",
          trailWidth: 1,
          svgStyle: { width: "100%", height: "100%" },
          text: { autoStyleContainer: false, value: "" },
        }
      );
    }
    if (!fatsBarInstance.current && document.getElementById("fatsBar")) {
      fatsBarInstance.current = new ProgressBar.Line(
        document.getElementById("fatsBar") as HTMLElement,
        {
          strokeWidth: 3,
          easing: "easeInOut",
          duration: 2100,
          color: "#6F975D",
          trailColor: "#eee",
          trailWidth: 1,
          svgStyle: { width: "100%", height: "100%" },
          text: { autoStyleContainer: false, value: "" },
        }
      );
    }
    initIMCCircle();
  }, []);

  // Actualiza los gráficos lineales y el círculo cuando cambia el perfil o la dieta seleccionada
  useEffect(() => {
    updateIMCCircle();
    if (proteinBarInstance.current) {
      const proteinVal = (profile.recommendedDailyProteins * 4) / 1672;
      proteinBarInstance.current.animate(proteinVal);
      const proteinPct = selectedDiet ? parsePercentageRange(selectedDiet.protein) : 35;
      proteinBarInstance.current.text.innerHTML = `${proteinPct}%`;
    }
    if (carboBarInstance.current) {
      const carboVal = (profile.recommendedDailyCarbos * 4) / 1672;
      carboBarInstance.current.animate(carboVal);
      const carbPct = selectedDiet ? parsePercentageRange(selectedDiet.carb) : 30;
      carboBarInstance.current.text.innerHTML = `${carbPct}%`;
    }
    if (fatsBarInstance.current) {
      const fatsVal = (profile.recommendedDailyFats * 9) / 1672;
      fatsBarInstance.current.animate(fatsVal);
      const fatPct = selectedDiet ? parsePercentageRange(selectedDiet.fats) : 35;
      fatsBarInstance.current.text.innerHTML = `${fatPct}%`;
    }
  }, [profile, selectedDiet]);

  // Al seleccionar una dieta, recalcula los valores de macros, actualiza el perfil y guarda en localStorage
  const handleDietSelect = (diet: Diet) => {
    console.log("Dieta seleccionada:", diet);
    const RC = profile.recommendedCaloriesPerDay;
    if (RC) {
      const proteinPct = diet.protein ? parsePercentageRange(diet.protein) : 0;
      const carbPct = diet.carb ? parsePercentageRange(diet.carb) : 0;
      const fatPct = diet.fats ? parsePercentageRange(diet.fats) : 0;
      const newProteins = Math.round((RC * proteinPct / 100) / 4);
      const newCarbos = Math.round((RC * carbPct / 100) / 4);
      const newFats = Math.round((RC * fatPct / 100) / 9);
      const updatedProfile: UserProfileData = {
        ...profile,
        recommendedDailyProteins: newProteins,
        recommendedDailyCarbos: newCarbos,
        recommendedDailyFats: newFats,
        selectedDiet: diet,
      };
      setProfile(updatedProfile);
      setSelectedDiet(diet);
      const storageKey = "userProfileData_" + (updatedProfile.email || user.email);
      localStorage.setItem(storageKey, JSON.stringify(updatedProfile));
      console.log("Perfil actualizado y guardado:", updatedProfile);
      setOpenDietPopup(false);
    }
  };

  // Función para obtener información de órdenes (se mantiene oculta)
  const getOrderInfoContent = (user: UserProfileData) => {
    return <div>Contenido de órdenes...</div>;
  };

  return (
    <div className="finalDashboard">
      <div
        className="userInfo"
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
          gap: "20px",
          flexWrap: "wrap",
          padding: "30px 0px",
          fontFamily: "Mukta, sans-serif",
        }}
      >
        {/* Columna IMC */}
        <div
          className="bmiInfo"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            width: "315px",
            boxSizing: "border-box",
            backgroundColor: "white",
            borderRadius: "35px",
            boxShadow: "rgba(0,0,0,0.1) 0px 4px 12px",
            height: "370px",
            padding: "40px",
          }}
        >
          <div>
            <h3 style={{ fontSize: "14px", fontWeight: "bold" }}>
              INDICE DE MASA
              <br />
              CORPORAL
            </h3>
          </div>
          <div
            className="circularChartContainer"
            style={{
              position: "relative",
              width: "150px",
              height: "150px",
              margin: "20px auto",
            }}
          >
            <div id="container2" ref={container2Ref} style={{ width: "100%", height: "100%" }}></div>
          </div>
          <div className="imcResult" style={{ textAlign: "center", marginTop: "10px" }}>
            <span>Tu IMC: </span>
            <span>{getIMCAnimation(profile.imc)[1]}</span>
          </div>
        </div>
        {/* Columna Calorías y Agua */}
        <div
          className="caloriesWater"
          style={{
            display: "flex",
            flexDirection: "column",
            width: "280px",
          }}
        >
          <div
            className="caloriesInfo"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "flex-start",
              backgroundColor: "#063741",
              borderRadius: "35px",
              boxShadow: "rgba(0,0,0,0.1) 0px 4px 12px",
              padding: "30px",
              color: "#ffffff",
              overflow: "hidden",
              marginTop: "25px",
            }}
          >
            <div className="absDivIcon" style={{ position: "absolute" }}>
              <div
                style={{
                  position: "absolute",
                  marginLeft: "180px",
                  width: "100px",
                  height: "100px",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="100px"
                  height="100px"
                  fill="#E0E470"
                  className="bi bi-fire"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 16c3.314 0 6-2 6-5.5 0-1.5-.5-4-2.5-6 .25 1.5-1.25 2-1.25 2C11 4 9 .5 6 0c.357 2 .5 4-2 6-1.25 1-2 2.729-2 4.5C2 14 4.686 16 8 16Zm0-1c-1.657 0-3-1-3-2.75 0-.75.25-2 1.25-3C6.125 10 7 10.5 7 10.5c-.375-1.25.5-3.25 2-3.5-.179 1-.25 2 1 3 .625.5 1 1.364 1 2.25C11 14 9.657 15 8 15Z" />
                </svg>
              </div>
            </div>
            <div>
              <h3 style={{ fontSize: "14px", fontWeight: "bold" }}>
                CALORIAS DIARIAS
                <br />
                RECOMENDADAS
              </h3>
            </div>
            <div className="amountDiv" style={{ display: "flex", flexDirection: "column" }}>
              <span
                className="caloriesAmount"
                style={{ fontSize: "32px", fontWeight: "bold", lineHeight: "29px" }}
              >
                {profile.recommendedCaloriesPerDay}
              </span>
              <span style={{ fontSize: "21px", lineHeight: "17px" }}>Kcal</span>
            </div>
          </div>
          <div
            className="waterInfo"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "flex-start",
              backgroundColor: "#85E0FF",
              borderRadius: "35px",
              boxShadow: "rgba(0,0,0,0.1) 0px 4px 12px",
              padding: "30px",
              color: "#001A63",
              overflow: "hidden",
              textAlign: "center",
              marginTop: "25px",
            }}
          >
            <div className="absDivIcon" style={{ position: "absolute" }}>
              <div
                style={{
                  position: "absolute",
                  marginLeft: "180px",
                  width: "100px",
                  height: "100px",
                }}
              >
                <svg
                  className="waterIcon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="100px"
                  height="100px"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 16a6 6 0 0 0 6-6c0-1.655-1.122-2.904-2.432-4.362C10.254 4.176 8.75 2.503 8 0c0 0-6 5.686-6 10a6 6 0 0 0 6 6ZM6.646 4.646l.708.708c-.29.29-1.128 1.311-1.907 2.87l-.894-.448c.82-1.641 1.717-2.753 2.093-3.13Z" />
                </svg>
              </div>
            </div>
            <div>
              <h3 style={{ fontSize: "14px", fontWeight: "bold", textAlign: "left" }}>
                AGUA DIARIA
                <br />
                RECOMENDADA
              </h3>
            </div>
            <div>
              <span style={{ fontSize: "23px", fontWeight: "bold" }}>
                {profile.recommendedDailyWater} Litros
              </span>
            </div>
          </div>
        </div>
        {/* Columna Distribución de macronutrientes */}
        <div
          className="macroInfo"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "space-between",
            width: "315px",
            height: "370px",
            padding: "40px",
            boxSizing: "border-box",
            boxShadow: "rgba(0,0,0,0.1) 0px 4px 12px",
            borderRadius: "35px",
            overflow: "hidden",
          }}
        >
          <div className="absDivIcon" style={{ position: "absolute" }}>
            <div
              style={{
                position: "absolute",
                marginLeft: "200px",
                width: "100px",
                height: "100px",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100px"
                height="100px"
                fill="#FD8502"
                className="bi bi-pie-chart-fill"
                viewBox="0 0 16 16"
              >
                <path d="M15.985 8.5H8.207l-5.5 5.5a8 8 0 0 0 13.277-5.5zM2 13.292A8 8 0 0 1 7.5.015v7.778l-5.5 5.5zM8.5.015V7.5h7.485A8.001 8.001 0 0 0 8.5.015z" />
              </svg>
            </div>
          </div>
          <div>
            <h3 style={{ fontSize: "14px", fontWeight: "bold", width: "90%", paddingBottom: '5px' }}>
              DISTRIBUCION DE MACRONUTRIENTES
              <br />
              RECOMENDADA
            </h3>
          </div>
          <div className="barsContainer">
            <div>
              <span>
                Carbohidratos - {profile.recommendedDailyCarbos} Grs -{" "}
                {selectedDiet ? `${parsePercentageRange(selectedDiet.carb)}%` : "30%"}
              </span>
              <div id="carboBar"></div>
            </div>
            <div>
              
              <span>
                Proteínas - {profile.recommendedDailyProteins} Grs -{" "}
                {selectedDiet ? `${parsePercentageRange(selectedDiet.protein)}%` : "35%"}
              </span>
              <div id="proteinBar"></div>
            </div>
            <div>
              <span>
                Grasas - {profile.recommendedDailyFats} Grs -{" "}
                {selectedDiet ? `${parsePercentageRange(selectedDiet.fats)}%` : "35%"}
              </span>
              <div id="fatsBar"></div>
            </div>
          </div>
          {/* Mostrar el tipo de dieta seleccionado en fucsia, 9px, bold */}
          <div
            style={{
              marginTop: "6px",
              color: "#7c67e2",
              fontSize: "0.9em",
              lineHeight: "1.1em",
              fontWeight: "bold",
            }}
          >
            {selectedDiet ? selectedDiet.title : DEFAULT_DIET.title}
          </div>
          <div
            style={{
              marginTop: "7px",
              fontSize: "0.8em",
              cursor: "pointer",
              color: "#856a8f",
              textDecoration: "underline",
              fontWeight: "bold",
            }}
            onClick={() => setOpenDietPopup(true)}
          >
            Cambiar tipo de dieta
          </div>
        </div>
      </div>
      <DietPopup
        open={openDietPopup}
        onClose={() => setOpenDietPopup(false)}
        onSelect={handleDietSelect}
      />
      <div className="ordersInfo" style={{ display: "none" }}>
        {getOrderInfoContent(profile)}
      </div>
    </div>
  );
};

export default UserProfile;
